import { Colors, TouchableOpacity, View } from "react-native-ui-lib";
import { useLayoutProvider } from "../../utils/layout";
import {
  PageItemType,
  useCreateTile,
  useEditTile,
  useLayoutEditController,
} from "../../../../utilities/layout";
import { FilterQuickKey, ProductQuickKey } from "./quick-keys";
import React from "react";
import GridView from "./components/GridView";
import Pager from "../../../../components/Pager/Pager";
import { AntDesign, Feather } from "@expo/vector-icons";
import { QuickKey } from "../QuickKey";
import { findIndex } from "lodash";
import { DragDropActionTypes } from "../../../../utilities/drag-drop";
import { MissingLayoutDimensionsError } from "./constants";
import { ActionItemRenderer } from "./renderers/ActionItemRenderer/ActionItemRenderer";
import { LayoutNodeType } from "@towersystems/roam-common/lib/generated-types";
import { JiggleView } from "../JiggleView";

export interface DynamicQuickKeyListV2 {}

export const Placeholder = ({ onAdd }: { onAdd?: () => void }) => {
  return (
    <QuickKey
      onPress={onAdd}
      style={{
        borderWidth: 2,
        borderStyle: "dashed",
        borderColor: "rgba(0,0,0,0.1)",
        height: 165,
        width: "100%",
      }}
      backgroundColor="rgba(0,0,0,0.05)"
      iconPrimary={
        <AntDesign name="pluscircleo" size={24} color="rgba(0,0,0,0.1)" />
      }
    />
  );
};

export const KeyOverlay = ({ children }: { children: React.ReactNode }) => {
  return (
    <View
      style={{
        pointerEvents: "none",
        width: "100%",
        height: 165,
      }}
    >
      {children}
    </View>
  );
};

export const EditKeyOverlay = ({
  children,
  onEdit,
}: {
  children: React.ReactNode;
  onEdit?: () => void;
}) => {
  const context = React.useContext(DynamicQuickKeyListV2Context);
  const { dragging } = context ?? { dragging: false };

  const handleOnEdit = () => {
    onEdit?.();
  };

  const editMarkup = (
    <TouchableOpacity
      onPress={handleOnEdit}
      style={{
        position: "absolute",
        top: 5,
        right: 5,
        width: 30,
        height: 30,
        zIndex: 100,
        backgroundColor: "white",
        borderRadius: 30,
        shadowColor: "#000",
        shadowOffset: {
          width: 0,
          height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
        elevation: 5,
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Feather name="edit-2" size={18} color="black" />
    </TouchableOpacity>
  );
  return (
    <View
      style={{
        width: "100%",
        height: 165,
        position: "relative",
      }}
    >
      {!dragging && editMarkup}
      <JiggleView jiggle={!dragging}>
        <KeyOverlay>{children}</KeyOverlay>
      </JiggleView>
    </View>
  );
};

export const DynamicQuickKeyListV2 = () => {
  const {
    layout,
    pagesController,
    layoutDimensions,
    isEditing: editing,
    startEditing,
    getProductById,
  } = useLayoutProvider();

  const performLayoutAction = useLayoutEditController();

  const { openCreateTile } = useCreateTile();
  const { openEditTile } = useEditTile();
  const isEditing: boolean = editing;
  const { currentPage, pages, goToPage, addEmptyPage } = pagesController;
  const [dragging, setDragging] = React.useState<boolean>(false);

  const handleSetDraggingFn = (dragging: boolean) => {
    setDragging(dragging);
  };

  const handleOnReleaseCell = (originalItems: PageItemType[], items: any) => {
    setDragging(false);
    const data = originalItems;

    const keys = items
      .filter((i: any) => i.item.placeholder == false)
      .filter((k: any) => {
        // remove any keys that have not been moved
        const originalIndex = findIndex(data, (d) => d.id === k.item.id);
        const newIndex = findIndex(items, (i: any) => i.item.id === k.item.id);

        if (originalIndex === newIndex) {
          return false;
        }

        return true;
      });

    if (keys.length === 0) {
      return;
    }

    const layoutId = layout?.id;
    if (!layoutId) {
      return;
    }

    const nodes = keys.map((k: any) => {
      return {
        id: k.item.id,
        position: findIndex(items, (i: any) => i.item.id === k.item.id),
      };
    });

    return performLayoutAction({
      type: DragDropActionTypes.UPDATE_LAYOUT_NODE_POSITIONS,
      payload: {
        layoutId,
        nodes,
      },
    }).catch(console.log);
  };

  const renderItemFn = ({ item, key }: { item: PageItemType; key: string }) => {
    let content = null;

    if (item.placeholder) {
      content =
        isEditing == true ? (
          <Placeholder
            onAdd={() => {
              if (!layout) {
                return;
              }
              const config = {
                layoutId: layout.id,
                position: item.position,
              };
              openCreateTile(config);
            }}
          />
        ) : (
          <View />
        );
    }

    if (item.config?.action) {
      content = (
        <ActionItemRenderer
          {...{ item }}
          quickKeyProps={{
            disabled: isEditing ? false : undefined,
            onLongPress: () => {
              startEditing();
            },
          }}
        />
      );
    }

    if (item.config?.productId) {
      content = (
        <ProductQuickKey
          product={getProductById(item.config.productId)}
          disabled={isEditing ? false : undefined}
          onLongPress={() => startEditing()}
          backgroundColor={item.config?.color || undefined}
          labelColor={item.config?.labelColor || undefined}
          backgroundImage={item.config.imageUrl}
          label={item.config.label}
        />
      );
    }

    if (item.config?.type === LayoutNodeType.FILTER) {
      content = (
        <FilterQuickKey
          item={item}
          disabled={isEditing ? false : undefined}
          onLongPress={() => startEditing()}
          backgroundColor={item.config?.color || undefined}
          labelColor={item.config?.labelColor || undefined}
          backgroundImage={item.config.imageUrl}
        />
      );
    }

    if ((!item.placeholder || !content) && isEditing) {
      content = (
        <EditKeyOverlay onEdit={() => openEditTile(item)}>
          {content}
        </EditKeyOverlay>
      );
    }

    return (
      <View
        key={key}
        style={{
          flexDirection: "row",
          width: "100%",
          height: 165,
        }}
      >
        {content}
      </View>
    );
  };

  const renderPageFn = ({ route }: any) => {
    const { key } = route;

    const item = pages.find((p) => p.page.toString() === key);
    if (!layoutDimensions || !item) throw new MissingLayoutDimensionsError();

    return (
      <GridView
        key={key}
        activeOpacity={0.8}
        delayLongPress={1}
        numColumns={layoutDimensions?.columns ?? 4}
        width={layoutDimensions?.width ?? undefined}
        data={item.items.map((i) => ({ item: i, key: i.id }))}
        renderItem={renderItemFn}
        keyExtractor={(item) => item.key}
        cellHeight={175}
        onBeginDragging={() => handleSetDraggingFn(true)}
        onReleaseCell={(updatedItems) =>
          handleOnReleaseCell(item.items, updatedItems)
        }
        draggble={(i) => {
          if (isEditing) {
            return i.item.placeholder == false;
          }
          return false;
        }}
        locked={(i) => {
          if (isEditing) {
            return false;
          }
          return i.item.placeholder == true;
        }}
        renderLockedItem={() => <View />}
      />
    );
  };

  const pagerMarkup = React.useMemo(() => {
    return (
      <Pager
        renderScene={renderPageFn}
        navigationState={{
          index: currentPage - 1,
          routes: pages.map((p) => ({
            key: p.page.toString(),
            title: "",
          })),
        }}
        onIndexChange={(index) => goToPage(index + 1)}
        paginationAdornment={
          isEditing ? (
            <TouchableOpacity
              onPress={addEmptyPage}
              style={{
                borderRadius: "50%",
                backgroundColor: Colors.neutralButton,
                margin: 6,
              }}
            >
              <AntDesign
                name="pluscircleo"
                size={24}
                color={Colors.$iconPrimary}
              />
            </TouchableOpacity>
          ) : undefined
        }
      ></Pager>
    );
  }, [currentPage, pages, isEditing]);

  return (
    <DynamicQuickKeyListV2Context.Provider value={{ dragging }}>
      {pagerMarkup}
    </DynamicQuickKeyListV2Context.Provider>
  );
};

export type DynamicQuickKeyListV2ContextType = {
  dragging: boolean;
};

export const DynamicQuickKeyListV2Context = React.createContext<
  DynamicQuickKeyListV2ContextType | undefined
>(undefined);
